import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  managers: [],
  paginatedManagers: [],
  selected_manager: null,
  loading: false,       // mutation operations (add / delete / block)
  fetchLoading: false,  // fetch operations
  hasFetched: false,
  recordsTotal: 0,
  errors: []
};

const normalizeErrors = (payload) =>
  Array.isArray(payload?.message)
    ? payload.message
    : [payload?.message].filter(Boolean);

export const fetchAllManagersFromCompany = createAsyncThunk(
  'manager/fetchAllFromCompany',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/manager/managerss');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchManagersPaginated = createAsyncThunk(
  'manager/fetchManagersPaginated',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/manager', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addManager = createAsyncThunk(
  'manager/addManager',
  async ({ email, phone, password, firstname, lastname }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/manager', { email, phone, password, firstname, lastname });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteManager = createAsyncThunk(
  'manager/deleteManager',
  async ({ managerId }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/manager/${managerId}`);
      return managerId;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const blockManager = createAsyncThunk(
  'manager/blockManager',
  async ({ managerId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/manager/block/${managerId}`);
      return { id: managerId, data: response.data };
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const formatManager = (manager) => ({
  ...manager,
  DT_RowId: manager._id,
  added_by: manager.added_by ? [manager.added_by] : []
});

const managerSlice = createSlice({
  name: 'manager',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchAllManagersFromCompany
      .addCase(fetchAllManagersFromCompany.pending, (state) => { state.fetchLoading = true; })
      .addCase(fetchAllManagersFromCompany.fulfilled, (state, { payload }) => {
        state.managers = payload.map(formatManager);
        state.recordsTotal = payload.length;
        state.fetchLoading = false;
        state.hasFetched = true;
      })
      .addCase(fetchAllManagersFromCompany.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload);
        state.fetchLoading = false;
      })

      // fetchManagersPaginated
      .addCase(fetchManagersPaginated.pending, (state) => { state.fetchLoading = true; })
      .addCase(fetchManagersPaginated.fulfilled, (state, { payload }) => {
        state.paginatedManagers = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
        state.fetchLoading = false;
      })
      .addCase(fetchManagersPaginated.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload);
        state.fetchLoading = false;
      })

      // addManager
      .addCase(addManager.pending, (state) => { state.loading = true; })
      .addCase(addManager.fulfilled, (state, { payload }) => {
        const formatted = formatManager(payload);
        state.managers.push(formatted);
        // ManagerTable now reads paginatedManagers (server-fetched), not
        // managers - mirror the add here too so a newly created manager
        // shows up without needing a manual refetch.
        state.paginatedManagers.unshift(formatted);
        state.recordsTotal += 1;
        state.loading = false;
      })
      .addCase(addManager.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload);
        state.loading = false;
      })

      // deleteManager — local splice, no refetch
      .addCase(deleteManager.pending, (state) => { state.loading = true; })
      .addCase(deleteManager.fulfilled, (state, { payload: id }) => {
        state.managers = state.managers.filter((m) => m._id !== id);
        state.loading = false;
      })
      .addCase(deleteManager.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload);
        state.loading = false;
      })

      // blockManager — prefer server response, fall back to toggle
      .addCase(blockManager.pending, (state) => { state.loading = true; })
      .addCase(blockManager.fulfilled, (state, { payload }) => {
        const idx = state.managers.findIndex((m) => m._id === payload.id);
        if (idx !== -1) {
          state.managers[idx].locked =
            payload.data?.locked !== undefined
              ? payload.data.locked
              : !state.managers[idx].locked;
        }
        state.loading = false;
      })
      .addCase(blockManager.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload);
        state.loading = false;
      });
  }
});

export default managerSlice.reducer;
